import React, { useState } from 'react';
import { AppLayout } from '../../components/layout/app-layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';

const TestLayoutPage: React.FC = () => {
  const [activeNavTab, setActiveNavTab] = useState('overview');

  const navbarTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'features', label: 'Features' },
    { id: 'demo', label: 'Demo' },
    { id: 'settings', label: 'Settings' },
  ];

  return (
    <AppLayout
      activePage="/test-layout"
      navbarProps={{
        showBackButton: true,
        backPath: "/dashboard",
        tabs: navbarTabs,
        activeTab: activeNavTab,
        onTabChange: setActiveNavTab,
      }}
    >
      <div className="space-y-6">
        {activeNavTab === 'overview' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">
              Layout Overview
            </h2>
            <p className="text-gray-600 mb-4">
              This is a test page to demonstrate the new AppLayout system with navbar tabs.
              The layout includes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-600">
              <li>
                Collapsible sidebar (try Ctrl/Cmd + B) with active page highlighting
              </li>
              <li>
                Responsive mobile-friendly design
              </li>
              <li>
                Top navigation bar with tab system (no more title!)
              </li>
              <li>
                Proper content spacing and overflow handling
              </li>
            </ul>
          </div>
        )}

        {activeNavTab === 'features' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">
              Features
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-900">
                  Desktop
                </h4>
                <p className="text-blue-700 text-sm">
                  Sidebar can be collapsed to icons or fully expanded
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h4 className="font-medium text-green-900">
                  Mobile
                </h4>
                <p className="text-green-700 text-sm">
                  Sidebar becomes an overlay sheet with mobile-friendly navigation
                </p>
              </div>
            </div>
          </div>
        )}

        {activeNavTab === 'demo' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">
              Demo Content
            </h2>
            <p className="text-gray-600 mb-4">
              This tab demonstrates how content can be switched based on navbar tab selection.
            </p>
          </div>
        )}

        {activeNavTab === 'settings' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">
              Settings
            </h2>
            <p className="text-gray-600 mb-4">
              This would be a settings panel in a real application.
            </p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-6">
            Multi-Tab Component Demo
          </h3>

          <Tabs
            defaultValue="overview"
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">
                Overview
              </TabsTrigger>
              <TabsTrigger value="analytics">
                Analytics
              </TabsTrigger>
              <TabsTrigger value="reports">
                Reports
              </TabsTrigger>
              <TabsTrigger value="settings">
                Settings
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value="overview"
              className="space-y-4"
            >
              <div className="rounded-lg border p-4">
                <h4 className="text-lg font-semibold mb-2">
                  Overview Dashboard
                </h4>
                <p className="text-gray-600 mb-4">
                  This is the overview tab content. It provides a high-level summary of key metrics and information.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h5 className="font-medium text-blue-900">
                      Total Users
                    </h5>
                    <p className="text-2xl font-bold text-blue-600">
                      12,345
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h5 className="font-medium text-green-900">
                      Active Sessions
                    </h5>
                    <p className="text-2xl font-bold text-green-600">
                      1,234
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h5 className="font-medium text-purple-900">
                      Revenue
                    </h5>
                    <p className="text-2xl font-bold text-purple-600">
                      $45,678
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent
              value="analytics"
              className="space-y-4"
            >
              <div className="rounded-lg border p-4">
                <h4 className="text-lg font-semibold mb-2">
                  Analytics & Insights
                </h4>
                <p className="text-gray-600 mb-4">
                  Detailed analytics and performance insights for your application.
                </p>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-medium mb-2">
                      User Engagement
                    </h5>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-blue-600 h-2.5 rounded-full"
                        style={{width: '75%'}}
                      />
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      75% engagement rate
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h5 className="font-medium mb-2">
                      Page Load Speed
                    </h5>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-green-600 h-2.5 rounded-full"
                        style={{width: '92%'}}
                      />
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      92% optimal performance
                    </p>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent
              value="reports"
              className="space-y-4"
            >
              <div className="rounded-lg border p-4">
                <h4 className="text-lg font-semibold mb-2">
                  Reports & Export
                </h4>
                <p className="text-gray-600 mb-4">
                  Generate and download various reports for your data analysis needs.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium">
                        Monthly User Report
                      </h5>
                      <p className="text-sm text-gray-600">
                        User activity and engagement metrics
                      </p>
                    </div>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                      Download
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium">
                        Revenue Analysis
                      </h5>
                      <p className="text-sm text-gray-600">
                        Financial performance and trends
                      </p>
                    </div>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                      Download
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <h5 className="font-medium">
                        System Performance
                      </h5>
                      <p className="text-sm text-gray-600">
                        Technical metrics and uptime data
                      </p>
                    </div>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                      Download
                    </button>
                  </div>
                </div>
              </div>
            </TabsContent>

            <TabsContent
              value="settings"
              className="space-y-4"
            >
              <div className="rounded-lg border p-4">
                <h4 className="text-lg font-semibold mb-2">
                  Tab Configuration
                </h4>
                <p className="text-gray-600 mb-4">
                  Configure and customize the behavior of your tabs component.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium">
                        Enable Animations
                      </h5>
                      <p className="text-sm text-gray-600">
                        Smooth transitions between tabs
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        defaultChecked
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium">
                        Lazy Load Content
                      </h5>
                      <p className="text-sm text-gray-600">
                        Load tab content only when needed
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                    </label>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h5 className="font-medium">
                        Remember Last Tab
                      </h5>
                      <p className="text-sm text-gray-600">
                        Persist active tab across sessions
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        defaultChecked
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                    </label>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-3">
            Sample Content
          </h3>
          <p className="text-gray-600 mb-4">
            This content demonstrates how the layout handles different amounts of content.
            The sidebar and navbar remain fixed while the main content scrolls.
          </p>
          {Array.from({ length: 3 }, (_, i) => (
            <div
              key={i}
              className="mb-4 p-4 bg-gray-50 rounded"
            >
              <h4 className="font-medium">
                Content Block
                {i + 1}
              </h4>
              <p className="text-gray-600 text-sm">
                This is sample content to demonstrate scrolling behavior.
                The layout should handle long content gracefully while keeping
                the navigation elements accessible.
              </p>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  );
};

export default TestLayoutPage;